import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Calendar, Plus, Trash2, Edit2, X, Clock, CheckCircle, AlertCircle, RotateCcw, Filter, Users, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SESSION_STATUSES = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-500/20 text-blue-400', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400', icon: AlertCircle },
  rescheduled: { label: 'Rescheduled', color: 'bg-yellow-500/20 text-yellow-400', icon: RotateCcw },
};

const CoachSessions = () => {
  const { getAuthHeaders } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState({
    athlete_id: '', session_type: 'private', date: '', time: '', 
    notes: '', focus_points: '', is_addon: false, addon_value: '', status: 'scheduled'
  });
  const [selectedAthletes, setSelectedAthletes] = useState([]);  // For group sessions
  const [showParticipants, setShowParticipants] = useState(null);  // Session ID to show participants
  const [participants, setParticipants] = useState(null);  // Participants data
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  // Check if current session type supports multiple athletes
  const isGroupType = formData.session_type === 'clinic' || formData.session_type === 'group';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sessionsRes, athletesRes] = await Promise.all([
        axios.get(`${API}/sessions`, { headers: getAuthHeaders() }),
        axios.get(`${API}/athletes`, { headers: getAuthHeaders() })
      ]);
      setSessions(sessionsRes.data);
      setAthletes(athletesRes.data);
    } catch (error) {
      toast.error('Failed to load sessions');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // For group sessions (clinic/group), use the group endpoint
      if (isGroupType && !editingSession && selectedAthletes.length > 0) {
        const payload = {
          athlete_ids: selectedAthletes,
          session_type: formData.session_type,
          date: formData.time ? `${formData.date}T${formData.time}` : formData.date,
          notes: formData.notes,
          focus_points: formData.focus_points,
          is_addon: formData.is_addon,
          addon_value: formData.is_addon && formData.addon_value ? parseFloat(formData.addon_value) : null
        };
        
        const response = await axios.post(`${API}/sessions/group`, payload, { headers: getAuthHeaders() });
        toast.success(`Session created for ${response.data.sessions_created} athletes. ${response.data.notifications_sent} notifications sent.`);
      } else {
        // Single athlete session (or editing)
        const payload = {
          ...formData,
          athlete_id: isGroupType && selectedAthletes.length > 0 ? selectedAthletes[0] : formData.athlete_id,
          date: formData.time ? `${formData.date}T${formData.time}` : formData.date,
          addon_value: formData.is_addon && formData.addon_value ? parseFloat(formData.addon_value) : null
        };
        
        if (editingSession) {
          await axios.put(`${API}/sessions/${editingSession.id}`, payload, { headers: getAuthHeaders() });
          toast.success('Session updated');
        } else {
          await axios.post(`${API}/sessions`, payload, { headers: getAuthHeaders() });
          toast.success('Session created');
        }
      }
      closeForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save session');
    }
    setSubmitting(false);
  };

  const handleEdit = (session) => {
    const sessionDate = session.date ? session.date.split('T')[0] : '';
    const sessionTime = session.date && session.date.includes('T') ? session.date.split('T')[1]?.substring(0, 5) : '';
    
    setEditingSession(session);
    setFormData({
      athlete_id: session.athlete_id,
      session_type: session.session_type,
      date: sessionDate,
      time: sessionTime,
      notes: session.notes || '',
      focus_points: session.focus_points || '',
      is_addon: session.is_addon || false,
      addon_value: session.addon_value || '',
      status: session.status || 'scheduled'
    });
    setShowForm(true);
  };

  const handleStatusChange = async (sessionId, newStatus) => {
    try {
      await axios.put(`${API}/sessions/${sessionId}`, { status: newStatus }, { headers: getAuthHeaders() });
      toast.success(`Session marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update session');
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm('Delete this session?')) return;
    try {
      await axios.delete(`${API}/sessions/${sessionId}`, { headers: getAuthHeaders() });
      toast.success('Session deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const toggleAthleteSelection = (athleteId) => {
    setSelectedAthletes(prev => 
      prev.includes(athleteId) 
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const selectAllAthletes = () => {
    setSelectedAthletes(athletes.map(a => a.id));
  };

  const clearAthleteSelection = () => {
    setSelectedAthletes([]);
  };

  const fetchParticipants = async (sessionId) => {
    setLoadingParticipants(true);
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}/participants`, { headers: getAuthHeaders() });
      setParticipants(res.data);
      setShowParticipants(sessionId);
    } catch (error) {
      toast.error('Failed to load participants');
    }
    setLoadingParticipants(false);
  };

  const closeParticipantsModal = () => {
    setShowParticipants(null);
    setParticipants(null);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingSession(null);
    setSelectedAthletes([]);
    setFormData({
      athlete_id: '', session_type: 'private', date: '', time: '',
      notes: '', focus_points: '', is_addon: false, addon_value: '', status: 'scheduled'
    });
  };

  const getAthleteName = (athleteId) => {
    const athlete = athletes.find(a => a.id === athleteId);
    return athlete?.name || 'Unknown';
  };

  const isUpcoming = (date) => {
    if (!date) return false;
    const sessionDate = new Date(date);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    return sessionDate.toDateString() === tomorrow.toDateString();
  };

  const isToday = (date) => {
    if (!date) return false;
    const sessionDate = new Date(date);
    const today = new Date();
    return sessionDate.toDateString() === today.toDateString();
  };

  const filteredSessions = sessions.filter(s => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'upcoming') {
      return (s.status === 'scheduled' || !s.status) && new Date(s.date) >= new Date();
    }
    return s.status === filterStatus;
  }).sort((a, b) => new Date(b.date) - new Date(a.date));

  const upcomingCount = sessions.filter(s => 
    (s.status === 'scheduled' || !s.status) && (isToday(s.date) || isUpcoming(s.date))
  ).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="coach-sessions">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Sessions</h1>
          <p className="text-muted-foreground">{sessions.length} total sessions</p>
        </div>
        <Button className="bg-primary text-black hover:bg-primary/90" onClick={() => setShowForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Session
        </Button>
      </div>

      {/* Upcoming Alert */}
      {upcomingCount > 0 && (
        <div className="bg-primary/10 border border-primary/30 p-4 rounded-sm flex items-center gap-3">
          <AlertCircle className="text-primary" size={20} />
          <span className="text-sm">
            <strong>{upcomingCount}</strong> session{upcomingCount > 1 ? 's' : ''} scheduled for today or tomorrow
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <Button 
          variant={filterStatus === 'all' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilterStatus('all')}
          className={filterStatus === 'all' ? 'bg-primary text-black' : ''}
        >
          All
        </Button>
        <Button 
          variant={filterStatus === 'upcoming' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilterStatus('upcoming')}
          className={filterStatus === 'upcoming' ? 'bg-primary text-black' : ''}
        >
          Upcoming
        </Button>
        {Object.entries(SESSION_STATUSES).map(([key, { label }]) => (
          <Button 
            key={key}
            variant={filterStatus === key ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilterStatus(key)}
            className={filterStatus === key ? 'bg-primary text-black' : ''}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 rounded-sm w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl uppercase">
                {editingSession ? 'Edit Session' : 'New Session'}
              </h2>
              <button onClick={closeForm} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Session Type - First so user knows if multi-select is available */}
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Session Type</label>
                <select
                  value={formData.session_type}
                  onChange={(e) => {
                    setFormData({...formData, session_type: e.target.value, athlete_id: ''});
                    setSelectedAthletes([]);
                  }}
                  className="w-full p-3 bg-transparent border border-border rounded-sm"
                >
                  <option value="private" className="bg-card">Private Session</option>
                  <option value="clinic" className="bg-card">Clinic</option>
                  <option value="zoom" className="bg-card">Online Session (Zoom)</option>
                  <option value="group" className="bg-card">Group Session</option>
                </select>
                {isGroupType && !editingSession && (
                  <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                    <Users size={12} /> You can select multiple athletes for this session type
                  </p>
                )}
              </div>
              
              {/* Athlete Selection - Changes based on session type */}
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">
                  {isGroupType && !editingSession ? 'Select Athletes' : 'Athlete'}
                </label>
                
                {isGroupType && !editingSession ? (
                  // Multi-select for clinic/group sessions
                  <div className="border border-border rounded-sm">
                    <div className="flex items-center justify-between p-2 border-b border-border bg-muted/20">
                      <span className="text-xs text-muted-foreground">
                        {selectedAthletes.length} of {athletes.length} selected
                      </span>
                      <div className="flex gap-2">
                        <button 
                          type="button" 
                          onClick={selectAllAthletes}
                          className="text-xs text-primary hover:underline"
                        >
                          Select All
                        </button>
                        <button 
                          type="button" 
                          onClick={clearAthleteSelection}
                          className="text-xs text-muted-foreground hover:text-foreground"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                      {athletes.map(athlete => (
                        <label 
                          key={athlete.id} 
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/30 ${
                            selectedAthletes.includes(athlete.id) ? 'bg-primary/10 border border-primary/30' : ''
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                            selectedAthletes.includes(athlete.id) 
                              ? 'bg-primary border-primary' 
                              : 'border-border'
                          }`}>
                            {selectedAthletes.includes(athlete.id) && (
                              <Check size={14} className="text-black" />
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={selectedAthletes.includes(athlete.id)}
                            onChange={() => toggleAthleteSelection(athlete.id)}
                            className="hidden"
                          />
                          <span className="text-sm">{athlete.name}</span>
                        </label>
                      ))}
                    </div>
                    {selectedAthletes.length === 0 && (
                      <p className="text-xs text-red-400 p-2">Please select at least one athlete</p>
                    )}
                  </div>
                ) : (
                  // Single select for private/zoom sessions or when editing
                  <select
                    value={formData.athlete_id}
                    onChange={(e) => setFormData({...formData, athlete_id: e.target.value})}
                    required
                    className="w-full p-3 bg-transparent border border-border rounded-sm"
                  >
                    <option value="" className="bg-card">Select Athlete</option>
                    {athletes.map(a => (
                      <option key={a.id} value={a.id} className="bg-card">{a.name}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Date</label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Time</label>
                  <Input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full p-3 bg-transparent border border-border rounded-sm"
                >
                  {Object.entries(SESSION_STATUSES).map(([key, { label }]) => (
                    <option key={key} value={key} className="bg-card">{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Focus Points</label>
                <Input
                  placeholder="Ball control, passing..."
                  value={formData.focus_points}
                  onChange={(e) => setFormData({...formData, focus_points: e.target.value})}
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Notes</label>
                <Textarea
                  placeholder="Session notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_addon}
                  onChange={(e) => setFormData({...formData, is_addon: e.target.checked, addon_value: e.target.checked ? formData.addon_value : ''})}
                />
                <span className="text-sm">Add-on session (extra charge, doesn't count against plan)</span>
              </label>

              {formData.is_addon && (
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Add-on Value ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="75.00"
                    value={formData.addon_value}
                    onChange={(e) => setFormData({...formData, addon_value: e.target.value})}
                    required={formData.is_addon}
                  />
                  <p className="text-xs text-muted-foreground mt-1">This will create a payment record when the session is completed</p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeForm} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting || (isGroupType && !editingSession && selectedAthletes.length === 0) || (!isGroupType && !formData.athlete_id && !editingSession)} 
                  className="flex-1 bg-primary text-black"
                >
                  {submitting ? 'Saving...' : editingSession ? 'Update' : isGroupType && selectedAthletes.length > 1 ? `Create for ${selectedAthletes.length} Athletes` : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sessions List */}
      {filteredSessions.length === 0 ? (
        <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
          <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">
            {filterStatus === 'all' ? 'No sessions yet' : `No ${filterStatus} sessions`}
          </p>
          {filterStatus === 'all' && (
            <Button onClick={() => setShowForm(true)}>Schedule Your First Session</Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredSessions.map((session) => {
            const status = SESSION_STATUSES[session.status] || SESSION_STATUSES.scheduled;
            const StatusIcon = status.icon;
            const upcoming = isUpcoming(session.date);
            const today = isToday(session.date);
            
            return (
              <div 
                key={session.id} 
                className={`bg-card border rounded-sm p-4 ${
                  today ? 'border-primary' : upcoming ? 'border-yellow-500/50' : 'border-border/40'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="font-medium">{getAthleteName(session.athlete_id)}</h3>
                      {!session.is_addon && (
                        <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded text-xs whitespace-nowrap" title="Sessions remaining">
                          {athletes.find(a => a.id === session.athlete_id)?.sessions_remaining || 0} sessions left
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${status.color}`}>
                        <StatusIcon className="inline mr-1" size={12} />
                        {status.label}
                      </span>
                      {/* Confirmation Status Badge */}
                      {session.confirmation_status === 'confirmed' && (
                        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs whitespace-nowrap flex items-center gap-1">
                          <CheckCircle size={12} /> Confirmed
                        </span>
                      )}
                      {session.confirmation_status === 'pending' && session.status === 'scheduled' && (
                        <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs whitespace-nowrap flex items-center gap-1">
                          <Clock size={12} /> Awaiting Confirmation
                        </span>
                      )}
                      {session.confirmation_status === 'declined' && (
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs whitespace-nowrap flex items-center gap-1">
                          <AlertCircle size={12} /> Declined
                        </span>
                      )}
                      {/* Group Session Badge */}
                      {session.group_session_id && (
                        <button
                          onClick={() => fetchParticipants(session.id)}
                          className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs whitespace-nowrap flex items-center gap-1 hover:bg-purple-500/30 transition-colors"
                          title="View all participants"
                        >
                          <Users size={12} /> Group
                        </button>
                      )}
                      {today && (
                        <span className="px-2 py-0.5 bg-primary/20 text-primary rounded text-xs whitespace-nowrap">
                          TODAY
                        </span>
                      )}
                      {upcoming && !today && (
                        <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs whitespace-nowrap">
                          TOMORROW
                        </span>
                      )}
                      {session.is_addon && (
                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs whitespace-nowrap">
                          Add-on {session.addon_value ? `$${session.addon_value}` : ''}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground capitalize">
                      {session.session_type} Session • {new Date(session.date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        hour: session.date?.includes('T') ? '2-digit' : undefined,
                        minute: session.date?.includes('T') ? '2-digit' : undefined
                      })}
                    </p>
                    {session.focus_points && (
                      <p className="text-sm text-muted-foreground mt-1">Focus: {session.focus_points}</p>
                    )}
                    {session.notes && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{session.notes}</p>
                    )}
                  </div>
                  
                  {/* Action buttons - stacked on mobile, inline on desktop */}
                  <div className="flex items-center gap-1 sm:ml-4 self-end sm:self-start">
                    {/* Quick status buttons */}
                    {session.status !== 'completed' && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleStatusChange(session.id, 'completed')}
                        className="text-green-400 hover:text-green-300 h-9 w-9"
                        title="Mark as completed"
                        data-testid={`complete-session-${session.id}`}
                      >
                        <CheckCircle size={18} />
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleEdit(session)}
                      className="text-muted-foreground hover:text-foreground h-9 w-9"
                      data-testid={`edit-session-${session.id}`}
                    >
                      <Edit2 size={18} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDelete(session.id)}
                      className="text-muted-foreground hover:text-red-400 h-9 w-9"
                      data-testid={`delete-session-${session.id}`}
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Participants Modal for Group Sessions */}
      {showParticipants && participants && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 rounded-sm w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl uppercase flex items-center gap-2">
                <Users size={20} className="text-purple-400" />
                Group Session Participants
              </h2>
              <button onClick={closeParticipantsModal} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>
            
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="bg-green-500/10 border border-green-500/30 p-3 rounded text-center">
                <p className="text-2xl font-bold text-green-400">{participants.confirmed || 0}</p>
                <p className="text-xs text-muted-foreground">Confirmed</p>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 rounded text-center">
                <p className="text-2xl font-bold text-yellow-400">{participants.pending || 0}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 p-3 rounded text-center">
                <p className="text-2xl font-bold text-red-400">{participants.declined || 0}</p>
                <p className="text-xs text-muted-foreground">Declined</p>
              </div>
            </div>

            {/* Participants List */}
            <div className="space-y-2">
              {participants.participants?.map((p) => (
                <div 
                  key={p.session_id} 
                  className={`flex items-center justify-between p-3 rounded border ${
                    p.confirmation_status === 'confirmed' ? 'bg-green-500/5 border-green-500/30' :
                    p.confirmation_status === 'declined' ? 'bg-red-500/5 border-red-500/30' :
                    'bg-yellow-500/5 border-yellow-500/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      p.confirmation_status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                      p.confirmation_status === 'declined' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {p.athlete_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="font-medium">{p.athlete_name}</p>
                      {p.confirmed_at && (
                        <p className="text-xs text-muted-foreground">
                          Confirmed: {new Date(p.confirmed_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {p.confirmation_status === 'confirmed' && (
                      <span className="flex items-center gap-1 text-green-400 text-sm">
                        <CheckCircle size={16} /> Confirmed
                      </span>
                    )}
                    {p.confirmation_status === 'pending' && (
                      <span className="flex items-center gap-1 text-yellow-400 text-sm">
                        <Clock size={16} /> Pending
                      </span>
                    )}
                    {p.confirmation_status === 'declined' && (
                      <span className="flex items-center gap-1 text-red-400 text-sm">
                        <AlertCircle size={16} /> Declined
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <Button 
              onClick={closeParticipantsModal} 
              className="w-full mt-4 bg-primary text-black"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachSessions;
